<template>
  <div
    v-if="!isLoading && contentType === 'onair' && (isError || !hasVodList)"
    class="vod-list-empty"
  >
    <img
      :src="require('@/assets/images/common/icon-error-74.png')"
      alt="에러 아이콘"
    />
    <p>본 프로그램은 Quick VOD만 제공됩니다.</p>
  </div>

  <div v-else-if="hasVodList" class="episode-list-layout">
    <div id="all" :class="['player-list', { loading: isLoading }]">
      <div class="filter-box">
        <button type="button" class="button" @click="changeFilterToggle">
          {{ filterText(orderData) }}
        </button>
      </div>

      <ul v-if="!isLoading" class="episode-list">
        <EpisodeCell
          v-for="(thumb, index) in vodList"
          :key="`episode_list_vod_${index}`"
          :thumb="thumb"
          :external-content-id="contentInfo?.contentid"
          :change-content="changeContent"
          :is-pooqzone="isWavveOn"
          :thumbnail-info="thumbnailInfo"
        />
        <li v-if="isOdd" class="vod-all-list-item" aria-hidden="true">
          &nbsp;
        </li>
      </ul>
    </div>

    <PagingComponent
      :current-page="currentPage"
      :page-count="pageCount"
      :limit="limit"
      :paging-count="10"
      :is-next-button-view="isNextButtonView"
    />
  </div>
</template>
<script setup>
  import { defineProps, computed, ref, onUnmounted } from 'vue'
  import { useRoute } from 'vue-router/composables'
  import { useRouterComposable } from '@/common/use/useRouterComposable'
  import EpisodeCell from './EpisodeCell.vue'
  import PagingComponent from '@/components/common/PagingComponent'
  import { getVodEpisodeList } from '@/common/utils/request'
  import cookie from '@/common/utils/cookie'
  import { parseUrl, stringify } from 'query-string'
  import { getThumbnailInfo } from '@/common/utils/common'
  import { PATH_NAME_PLAYER_STREAM } from '@/common/resources/router.const'
  import { EventBus } from '@/common/utils/event-bus'

  const ORDER_TEXT = {
    NEWEST: '최신순',
    OLDEST: '오래된순',
  }
  const ORDER = {
    NEW: 'new',
    OLD: 'old',
  }
  const DEFAULT = {
    OFFSET: 0,
    LIMIT: 10,
    CURRENT_PAGE: 1,
    EPISODE_DISPLAY_COUNT: 10,
  }
  const ERROR = {
    MESSAGE_EMPTY_EPISODE_LIST: '에피소드 리스트를 가져오지 못했습니다.',
    MESSAGE_EMPTY_CELL_LIST: 'Empty Cell List',
  }

  const route = useRoute()
  const { pushRouter, replaceRouter, routerBack } = useRouterComposable()

  const props = defineProps({
    contentInfo: {
      type: Object,
      default: () => ({}),
    },
    landingInfo: {
      type: Object,
      default: () => ({}),
    },
  })

  const vodList = ref([])
  const currentPage = ref(1)
  const pageCount = ref(0)
  const limit = ref(10)
  const offset = ref(0)
  const orderData = ref('')
  const isNextButtonView = ref(false)
  const thumbnailInfo = ref({})
  const isOdd = ref(false)
  const isError = ref(false)
  const isLoading = ref(true)
  const errorTimer = ref(null)

  const isWavveOn = computed(() => cookie.isWavveOn())
  const contentType = computed(() => props.contentInfo?.type ?? '')
  const hasVodList = computed(() => vodList.value.length !== 0)

  const changeQueryRouterReplace = (changeQueryObj) => {
    const currentQuery = route?.query || {}
    const newQuery = { ...currentQuery, ...changeQueryObj }

    replaceRouter({ path: `vod?${stringify(newQuery)}` })
  }

  const filterText = (orderText) => {
    return orderText === ORDER.NEW ? ORDER_TEXT.NEWEST : ORDER_TEXT.OLDEST
  }

  const changeFilterToggle = () => {
    orderData.value = orderData.value === ORDER.NEW ? ORDER.OLD : ORDER.NEW
    filterText(orderData.value)
    offset.value = DEFAULT.OFFSET
    limit.value = DEFAULT.LIMIT
    currentPage.value = DEFAULT.CURRENT_PAGE

    changeQueryRouterReplace({
      orderby: orderData.value,
      page: currentPage.value,
    })
  }

  const changeContent = (thumb) => {
    const { contentid = '' } = thumb

    pushRouter({
      name: PATH_NAME_PLAYER_STREAM,
      params: { section: 'vod' },
      query: { contentid: contentid },
    })
  }

  const handleError = (error) => {
    if (error) {
      console.error(`Error:: Couldn't Get Vod Episode List :${error.message}`)
    }
    EventBus.$emit('OPEN_PPV_TOAST', () => {}, ERROR.MESSAGE_EMPTY_EPISODE_LIST)
    errorTimer.value = setTimeout(() => routerBack(), 3000)
  }

  const isNextPageButton = () => {
    const digit = currentPage.value % 10
    const remainCount = digit === 0 ? 1 : 11 - digit

    return remainCount * DEFAULT.EPISODE_DISPLAY_COUNT <= pageCount.value
  }

  const setCurrentPage = () => {
    currentPage.value = Number(route.query?.page || '1')
  }

  const getPayloadData = () => {
    const { landing_list: landingList = {}, programid = '' } = props.landingInfo
    const [episodeTab] = landingList.tab.filter(
      (tab) => tab?.type === 'episode',
    )
    const episodeTabPath = episodeTab?.path.replace('/fz', '') || ''
    const { url, query } = parseUrl(episodeTabPath)

    offset.value = limit.value * (currentPage.value - 1) ?? 0
    orderData.value = route.query?.orderby || query?.orderby

    return {
      url,
      orderBy: orderData.value,
      programId: programid ?? '',
    }
  }

  const setData = async () => {
    isLoading.value = true
    const { url, orderBy, programId } = getPayloadData()

    try {
      const { data } = await getVodEpisodeList({
        path: url,
        limit: limit.value,
        offset: offset.value,
        orderby: orderBy,
        programid: programId,
      })

      const cellList = data?.cell_toplist?.celllist ?? []

      if (cellList.length === 0) {
        const error = new Error(ERROR.MESSAGE_EMPTY_CELL_LIST)
        handleError(error)
        return
      }

      pageCount.value = Number(data?.cell_toplist?.pagecount) ?? 0
      thumbnailInfo.value = getThumbnailInfo(data?.cell_toplist?.cell_type)
      isNextButtonView.value = isNextPageButton()
      vodList.value = cellList ?? []
      isOdd.value = cellList?.length % 2 === 1
    } catch (error) {
      handleError(error)
    } finally {
      isLoading.value = false
    }
  }

  const init = () => {
    setCurrentPage()
    setData()
  }

  init()

  onUnmounted(() => (errorTimer.value = null))
</script>
<style lang="scss" scoped>
  .episode-list-layout {
    margin: -20px 100px 0px;
    overflow: hidden;
    .player-list {
      margin: 0 0 40px;
      overflow: hidden;
    }
  }
  .player-list {
    &.loading {
      min-height: 1159px;
    }
  }
</style>
<!--todo: 다운로드 버튼 히스토리 확인-->
